import { Stack } from '@cp/ds/src/components/stack';
import { useCallback, useEffect } from 'react';
import { useRouter } from '@cp/shared/router';
import { generateAdminPath } from 'src/app/shared/utils/generateAdminPath';
import { AdminRoutePath } from 'src/entities/adminRoute';
import { useDebouncedCallback } from 'use-debounce';
import { useGetQueryParams } from '@cp/shared/queryParams';
import {
  ProcessingAndReviewsFiltersType,
  ProcessingAndReviewsQueryParams,
  filtersSchema,
  useProcessingAndReviews,
} from '../processingAndReviews';
import { useProcessingCounters } from '../processingCountersProvider';
import { SearchFields } from './searchFields';
import { ItemsType } from './itemsType';
import { ClientTypeField } from './clientTypeField';

export const ProcessingAndReviewsFilters = () => {
  const router = useRouter();
  const { form } = useProcessingAndReviews();
  const { refetch } = useProcessingCounters();
  const queryParams = useGetQueryParams<ProcessingAndReviewsQueryParams>();

  const updateQuery = useCallback(
    (data: ProcessingAndReviewsFiltersType) => {
      const query = { ...data };

      Object.keys(query).map((key) => {
        if (!query[key]) {
          delete query[key];
        }
      });

      router.navigate(
        generateAdminPath(AdminRoutePath.ProcessingAndReviews, {
          queryParams: query,
        }),
      );
    },
    [router],
  );
  const debouncedUpdateQuery = useDebouncedCallback(updateQuery, 400);

  useEffect(() => {
    const debouncedKeys: Array<keyof ProcessingAndReviewsFiltersType> = ['phoneOrEmail'];
    const keys = Object.keys(filtersSchema.fields).filter((key) => !debouncedKeys.includes(key));

    const debouncedUnsubscribies = debouncedKeys.map((key) =>
      form.subscribe({
        name: key,
        formState: {
          values: true,
        },
        callback: ({ values }) => {
          debouncedUpdateQuery(values);
        },
      }),
    );
    const unsubscribies = keys.map((key) =>
      form.subscribe({
        name: key,
        formState: {
          values: true,
        },
        callback: ({ values }) => {
          updateQuery(values);
        },
      }),
    );

    return () => {
      debouncedUnsubscribies.map((u) => u());
      unsubscribies.map((u) => u());
    };
  }, [debouncedUpdateQuery, form, router, updateQuery]);

  useEffect(() => {
    updateQuery(form.getValues());
    refetch();
  }, [queryParams]);

  return (
    <Stack component="form" spacing={2.5}>
      <SearchFields />
      <ClientTypeField />
      <ItemsType />
    </Stack>
  );
};
